import React, { useEffect, useMemo, useState } from "react";
import { getOrderHistory } from "@/api/orders.js";
import { formatValue } from "@/utils/Utils.js";

const formatHistoryTimestamp = (timestamp, timezone) => {
  if (!timestamp) return "-";

  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || undefined,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(new Date(timestamp));
  } catch (e) {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(new Date(timestamp));
  }
};

const MAX_ITEM_CHANGES_TO_SHOW = 3;

const ItemChangeRow = ({ item, idx }) => {
  const title = item?.title || item?.sku || "Item";
  const sku = item?.sku;
  const showSku = sku && sku !== title;
  const subtitle = item?.subtitle;
  const from = item?.from;
  const to = item?.to;

  return (
    <div
      key={item?.sku || `${title}-${idx}`}
      className="text-sm text-slate-600 dark:text-slate-300"
    >
      <div className="font-medium">{title}</div>
      {showSku ? (
        <div className="text-xs text-slate-500 dark:text-slate-400">{sku}</div>
      ) : null}
      {subtitle ? (
        <div className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</div>
      ) : null}
      {from != null && to != null ? (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {from} -&gt; {to}
        </div>
      ) : null}
    </div>
  );
};

const ItemChangeSection = ({ label, labelClassName, items }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = items.length > MAX_ITEM_CHANGES_TO_SHOW;
  const visibleItems =
    expanded || !hasMore ? items : items.slice(0, MAX_ITEM_CHANGES_TO_SHOW);
  const hiddenCount = items.length - MAX_ITEM_CHANGES_TO_SHOW;

  return (
    <div>
      <div className={`text-xs font-medium mb-1 ${labelClassName}`}>{label}</div>
      <div className="flex flex-col gap-1">
        {visibleItems.map((item, idx) => (
          <ItemChangeRow key={item?.sku || `${label}-${idx}`} item={item} idx={idx} />
        ))}
      </div>
      {hasMore && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
        >
          +{hiddenCount} more
        </button>
      )}
      {hasMore && expanded && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
        >
          Show less
        </button>
      )}
    </div>
  );
};

const ItemChangesDescription = ({ description }) => {
  const addedItems = Array.isArray(description.added) ? description.added : [];
  const removedItems = Array.isArray(description.removed) ? description.removed : [];

  return (
    <div className="flex flex-col gap-2">
      {addedItems.length > 0 && (
        <ItemChangeSection
          label="Added"
          labelClassName="text-green-600 dark:text-green-400"
          items={addedItems}
        />
      )}
      {removedItems.length > 0 && (
        <ItemChangeSection
          label="Removed"
          labelClassName="text-red-600 dark:text-red-400"
          items={removedItems}
        />
      )}
    </div>
  );
};

const renderHistoryDescription = (description) => {
  if (!description) return null;

  if (typeof description === "string") {
    return (
      <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">
        {description}
      </div>
    );
  }

  if (
    typeof description === "object" &&
    (Array.isArray(description.added) || Array.isArray(description.removed))
  ) {
    return <ItemChangesDescription description={description} />;
  }

  return (
    <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">
      {typeof description === "object"
        ? JSON.stringify(description)
        : String(description)}
    </div>
  );
};

const OrderHistoryTimeline = ({
  orderId,
  timezone,
  isActive,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState(null);
  const [lastFetchedKey, setLastFetchedKey] = useState(null);

  useEffect(() => {
    setOrderHistory(null);
    setLoading(false);
    setLastFetchedKey(null);
  }, [orderId]);

  useEffect(() => {
    if (!isActive) return;
    if (!orderId) return;

    const fetchKey = `${orderId}:${refreshKey}`;
    if (lastFetchedKey === fetchKey) return;

    let cancelled = false;
    setLoading(true);

    getOrderHistory(orderId)
      .then((res) => {
        if (cancelled) return;
        setOrderHistory(res);
        setLastFetchedKey(fetchKey);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch order history:", err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isActive, orderId, refreshKey, lastFetchedKey]);

  const historyItems = useMemo(() => {
    if (Array.isArray(orderHistory)) return orderHistory;
    return orderHistory?.history || [];
  }, [orderHistory]);

  return (
    <div className="flex flex-col w-full pb-2">
      <div className="text-xs text-slate-500 dark:text-slate-400 italic mb-3">
        Order history is retained for 90 days. All times are displayed in the
        store&apos;s timezone.
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center mt-6 mb-6 ml-4 mr-4 gap-5">
          <svg
            className="animate-spin w-16 h-16 fill-current shrink-0"
            viewBox="0 0 16 16"
          >
            <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
          </svg>
          <div className="text-sm text-slate-500">Loading history...</div>
        </div>
      ) : historyItems.length === 0 ? (
        <div className="text-sm text-slate-500">No history available.</div>
      ) : (
        <div>
          <div className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-4">
            Timeline
          </div>

          <div className="relative pl-10">
            <div className="absolute left-[18px] top-2 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />

            {historyItems.map((entry, idx) => {
              const refundAmountCents = entry?.to?.refund_amount_cents;
              const refundAmountDollars =
                refundAmountCents != null
                  ? Number(refundAmountCents) / 100
                  : null;
              const header =
                refundAmountDollars != null
                  ? `${formatValue(refundAmountDollars)} refunded`
                  : entry?.event || "-";
              const actor = entry?.merchant_user_name || entry?.merchant_user;

              return (
                <div key={entry._id || `${entry.event}-${idx}`} className="relative mb-6">
                  <div
                    className="absolute left-[-34px] top-2 w-6 h-6 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600"
                    aria-hidden="true"
                  >
                    <div className="absolute inset-0 m-auto w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {header}
                    </div>

                    {actor && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {actor}
                      </div>
                    )}

                    {renderHistoryDescription(entry?.description)}

                    {entry?.to?.refund_id && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Refund ID: {String(entry.to.refund_id)}
                      </div>
                    )}

                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {formatHistoryTimestamp(entry?.timestamp, timezone)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistoryTimeline;
